import CardStatus from './CardStatus';
import dayjs from 'dayjs';
import { Button, Card, Flex, HStack, Icon, Link, List, Spacer, Text, Wrap } from '@chakra-ui/react';
import { formatLesson, formatLocation, formatRescheduledLesson, formatStudentGrade, formatStudentLevel, formatStudentSchool, formatSubjectCode, getLessonTypeForCode } from '@opanuj/shared/utils';
import { Lesson, LessonStatus, Location } from '@opanuj/shared/types';
import { lessonDelete } from '../../../../operations/admin/lessonDelete';
import { lessonRescheduleAccept } from '../../../../operations/admin/lessonRescheduleAccept';
import { lessonRescheduleReject } from '../../../../operations/admin/lessonRescheduleReject';
import { lessonStatusUpdate } from '../../../../operations/admin/lessonStatusUpdate';
import { LessonWithClient } from '../LessonsController';
import { Link as RouterLink } from 'react-router-dom';
import { memo, useState } from 'react';
import { PiStudentDuotone } from 'react-icons/pi';
import { Tooltip } from '../../../../components/ui/tooltip';
import { useDatabase } from '../../../../hooks/useDatabase';

const backgroundForStatus = (status: LessonStatus) => {
	switch (status) {
		case LessonStatus.Cancelled:
			return 'orange.50';
		case LessonStatus.Extra:
			return 'blue.50';
		case LessonStatus.Rescheduled:
			return 'purple.50';
		case LessonStatus.Regular:
		default:
			return 'green.50';
	}
};

// const colorForStatus = (status: LessonStatus) => {
// 	switch (status) {
// 		case LessonStatus.Regular:
// 			return 'green';
// 		case LessonStatus.Cancelled:
// 			return 'orange';
// 		case LessonStatus.Extra:
// 			return 'blue';
// 		default:
// 			return 'gray';
// 	}
// }

function LessonCard({ lesson, selected = false, onSelect }: { lesson: LessonWithClient; selected?: boolean; onSelect?: (lessonId: string) => void }) {
	const location = lesson.location !== Location.Online
		? `${lesson.location} (${lesson.room})`
		: formatLocation(lesson.location);
	const timestamp = dayjs(lesson.timestamp * 1000).locale('pl').format('H:mm')
	const title = `${formatSubjectCode(lesson.code)} • ${getLessonTypeForCode(lesson.code)} • ${lesson.teacher} • ${timestamp} (${lesson.duration}min) • ${location}`;
	type CardMode = 'view' | 'canceling' | 'restoring' | 'editing' | 'accepting' | 'deleting' | 'rejectingReschedule' | 'acceptingReschedule';
	
	const database = useDatabase();
	const [cardMode, setCardMode] = useState<CardMode>('view');
	const [rescheduleOption, setRescheduleOption] = useState<Lesson | null>(null);

	const handleDelete = () => {
		return lessonDelete(database, { id: lesson.id } );
	};
	const handleCancel = () => {
		return lessonStatusUpdate(database, { id: lesson.id, status: LessonStatus.Cancelled });
	};
	const handleRestore = () => {
		return lessonStatusUpdate(database, {
			id: lesson.id,
			status: LessonStatus.Regular,
		});
	};
	const handleRescheduleRejection = () => {
		return lessonRescheduleReject({ lessonId: lesson.id });
	};
	const handleRescheduleAcceptance = (option: Lesson) => {
		return lessonRescheduleAccept({ lessonId: lesson.id, optionId: option.id });
	};

	return (
        <Card.Root
			size="sm"
			w="100%"
			borderWidth={2}
			borderColor={selected ? 'blue.500' : 'gray.100'}
			cursor={onSelect ? 'pointer' : undefined}
			_hover={onSelect && !selected ? { borderColor: 'blue.200' } : undefined}
			onClick={onSelect ? () => onSelect(lesson.id) : undefined}
		>
			<Card.Header px={3} py={2} borderBottomWidth={1} borderColor="gray.200" bg={backgroundForStatus(lesson.status)}>
				<Flex alignItems="center">
					<Text color="gray.600" fontWeight="bold" letterSpacing="wide" textTransform="uppercase" fontSize="sm">{title}</Text>
					<Spacer />
					<CardStatus status={lesson.status} hasRescheduleOptions={!!lesson.rescheduleOptions}/>
					{/* {cardMode === 'view' ? (
						<CardMenu
							status={lesson.status}
							onRestore={() => setCardMode('restoring')}
							onCancel={() => setCardMode('canceling')}
							onDelete={() => setCardMode('deleting')}
						/>
					) : (
						<IconButton
							variant="outline"
							aria-label="close"
							onClick={() => setCardMode('view')}
						>
							<FiX />
						</IconButton>
					)} */}
				</Flex>
			</Card.Header>
            {cardMode === 'view' && (
				<Card.Body px={4} py={2}>
					<HStack gap={2} align="start">
						{lesson.students.map((student, index) => {
							const clientLabel = `klient: ${student.client.name}`;
							const studentDetails = [formatStudentSchool(student), formatStudentGrade(student), formatStudentLevel(student)].filter(x => x.length).join(' ');
							return (
								<Tooltip key={index} content={clientLabel} positioning={{ placement: 'top' }}>
									<Link
										asChild
										display="inline-flex"
										alignItems="center"
										gap={1.5}
										aria-label={`Otwórz klienta ${student.client.name}`}
										_hover={{ color: 'orange.600' }}
									>
										<RouterLink to={`/admin/klienci/${student.clientId}`}>
											<Icon as={PiStudentDuotone} />
											<Text fontWeight="bold" color="gray.700">{student.name}</Text>
											{studentDetails && <Text fontStyle='italic' color="gray.700">- {studentDetails}</Text>}
										</RouterLink>
									</Link>
								</Tooltip>
                            );
						})}
					</HStack>
				</Card.Body>
			)}
            {cardMode === 'deleting' && (
				<Card.Body>
					<Flex>
						<Text textStyle="italic">
							Czy na pewno chcesz usunąć tą lekcję?
						</Text>
						<Spacer />
						<Button
							onClick={() => {
								handleDelete();
								setCardMode('view');
							}}
							colorPalette="red"
						>
							Usuń
						</Button>
					</Flex>
				</Card.Body>
			)}
            {cardMode === 'canceling' && (
				<Card.Body>
					<Flex>
						<Text textStyle="italic">
							Czy na pewno chcesz odwołać tą lekcję?
						</Text>
						<Spacer />
						<Button
							onClick={() => {
								handleCancel();
								setCardMode('view');
							}}
							colorPalette="yellow"
						>
							Odwołaj
						</Button>
					</Flex>
				</Card.Body>
			)}
            {cardMode === 'restoring' && (
				<Card.Body>
					<Flex>
						<Text textStyle="italic">
							Czy na pewno chcesz przywrócić tą lekcję?
						</Text>
						<Spacer />
						<Button
							onClick={() => {
								handleRestore();
								setCardMode('view');
							}}
							colorPalette="green"
						>
							Przywróć
						</Button>
					</Flex>
				</Card.Body>
			)}
            {cardMode === 'rejectingReschedule' && (
				<Card.Body>
					<Flex>
						<Text textStyle="italic">
							Czy na pewno chcesz odrzucić wszystkie propozycje?
						</Text>
						<Spacer />
						<Button
							onClick={() => {
								handleRescheduleRejection();
								setCardMode('view');
							}}
							colorPalette="red"
						>
							Odrzuć wszystkie
						</Button>
					</Flex>
				</Card.Body>
			)}
            {cardMode === 'acceptingReschedule' && (
				<Card.Body>
					<Flex>
						<Text textStyle="italic">
							{`Czy na pewno chcesz zaakceptować ${formatLesson(rescheduleOption!)}?`}
						</Text>
						<Spacer />
						<Button
							onClick={() => {
								handleRescheduleAcceptance(rescheduleOption!);
								setRescheduleOption(null);
								setCardMode('view');
							}}
							colorPalette="orange"
						>
							Zaakceptuj
						</Button>
					</Flex>
				</Card.Body>
			)}
            {lesson.status === LessonStatus.Cancelled && lesson.rescheduleOptions && (
				<Card.Body>
					<Wrap alignItems='center' mb={2}>
						<Text fontSize='md'>Propozycje terminów odrobienia zajęć:</Text>
						<Button
							size="xs"
							mx={2}
							onClick={() => setCardMode('rejectingReschedule')}
						>
							Odrzuć wszystkie
						</Button>
					</Wrap>
					<List.Root as='ul' listStylePosition={"inside"}>
						{lesson.rescheduleOptions.map((option) => (
							<List.Item key={option.id} mt={2}>
									{formatRescheduledLesson(option)}
									<Button
										size="xs"
										mx={2}
										onClick={() => {
											setCardMode('acceptingReschedule')
											setRescheduleOption(option);
										}}
									>
										Zaakceptuj
									</Button>
							</List.Item>

						))}
					</List.Root>
				</Card.Body>
			)}
        </Card.Root>
    );
}

export default memo(LessonCard);
